import { Button, Form, Input, message, Modal, Select } from 'antd';
import type { ChangeEvent } from 'react';
import { useImperativeHandle } from 'react';
import { forwardRef, useState } from 'react';
import Url from '@/utils/url';
import styles from './index.module.scss';
import { useCount } from '@/hooks/useCount';
import { observer } from 'mobx-react-lite';
import { useStore } from '@/store/store';
import { codeLogin, getAreaCodeList, getCode } from '@/store/services/user';
import { useRouter } from 'next/router';
import { useRequest } from 'ahooks';

const { useForm } = Form;

const isProd = process.env.PROD;

export const PhoneInput = ({
  onChange,
  area_data,
  value,
  disabled,
  ple,
}: {
  value?;
  onChange?;
  area_data;
  disabled: boolean;
  ple;
}) => {
  const [input, setInput] = useState('');
  const [selectValue, setSelectValue] = useState<string | number>(1000);

  const select = (v: number) => {
    setSelectValue(v);
    onChange({ area_code: v, phone: input });
  };

  const changeInput = (e) => {
    setInput(e.target.value);
    onChange({ area_code: selectValue, phone: e.target.value.trim() });
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: '#E2E4EA 1px solid',
        width: '100%',
        paddingBottom: 4,
      }}
    >
      <Select
        optionFilterProp="label"
        disabled={disabled}
        placeholder="请选择区号"
        style={{ width: 110 }}
        onChange={select}
        value={value?.area_code || selectValue}
      >
        {area_data?.data.map((item) => (
          <Select.Option key={item.id} value={item.id} label={item.name}>
            {item.name}
          </Select.Option>
        ))}
      </Select>
      <div style={{ height: 28, width: 1, background: 'rgb(226, 228, 234)', marginRight: 8 }} />
      <Input
        style={{ flex: 1 }}
        bordered={false}
        disabled={disabled}
        onChange={changeInput}
        value={value?.phone || input}
        placeholder={ple || '请输入手机号'}
      />
    </div>
  );
};

const CodeInput = ({
  onChange,
  onGetCode,
  value,
  canGet = true,
}: {
  value?;
  onChange?;
  onGetCode?;
  canGet?;
}) => {
  const { count, go, isCanGo } = useCount(60);
  const [inputValue, setInputValue] = useState('');

  const sendMsg = async () => {
    const isPass = await onGetCode();
    if (isPass) go();
  };

  const codeBtn = (
    <button
      className={styles.code_input_btn}
      onClick={sendMsg}
      disabled={!isCanGo || !canGet}
      type="button"
    >
      获取验证码{isCanGo ? '' : count}
    </button>
  );

  const change = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onChange?.(e.target.value);
  };

  return (
    <div style={{ borderBottom: '#E2E4EA 1px solid', paddingBottom: 4 }}>
      <Input
        placeholder="请输入手机验证码"
        value={value ?? inputValue}
        addonAfter={codeBtn}
        onChange={change}
        bordered={false}
      />
    </div>
  );
};

const ConfirmPhoneModal = forwardRef<
  { show: () => void },
  { examId?: string; anid?: string; onConfirm?: any; callback?: (arg?: any) => void }
>(({ callback }, ref) => {
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [form] = useForm();
  const {
    userStore: { confirmModalShow },
    userStore,
  } = useStore();
  const { data: area_data } = useRequest(getAreaCodeList);
  const { homeStore } = useStore();
  // 是否是测评
  const isEvaluation = !!homeStore.examDetail?.is_evaluation;

  const router = useRouter();
  const anid = router.query.anid;
  const examId = router.query.paper;
  const isHaveAnidInfo = !!userStore.anidInfo;

  const onSubmit = async () => {
    // const isBeiSenBindPhone = ['beisen', 'moka'].includes(userStore.anidInfo?.platform);
    setLoading(true);
    try {
      const data = await form.validateFields();
      const res = await codeLogin({
        ...data,
        ...data.phone_areaCode,
        anid,
        exam: examId,
        tianc: isProd ? '' : 2612203042567290,
      });
      setLoading(false);
      if (![0, 'success'].includes(res.status)) {
        message.warning(res.message, 10);
        throw new Error('登陆错误');
      }
      callback?.({ data: (res as any).login });
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const onGetCode = async () => {
    const { phone_areaCode } = await form.validateFields(['phone_areaCode']);
    const { phone, area_code } = phone_areaCode;
    const { message: msg, status } = await getCode({
      phone,
      area_code,
      is_maotai: Number(homeStore.hideLogoId.includes(homeStore.examId!)),
      type: isEvaluation ? 'evaluation' : undefined,
    });
    if (status === 'success') {
      message.success(msg);
      return true;
    }
    message.warning(msg);
  };

  const show = () => {
    // setVisible(true);
  };

  const close = () => {
    // setVisible(false);
  };

  useImperativeHandle(ref, () => ({
    show,
    close,
  }));

  return (
    <Modal
      open={confirmModalShow}
      width={820}
      bodyStyle={{
        padding: 50,
      }}
      footer={null}
      closable={true}
      destroyOnClose
      onCancel={() => userStore.setConfirmModalShow(false)}
    >
      <div className={styles.main}>
        <div className={styles.left}>
          <img
            src={
              Url.staticHost +
              (isEvaluation
                ? '/image/home/confirmPhoneEvaluation.png?v=1'
                : '/image/home/confirmPhone.png?v=1')
            }
            alt={''}
            width={320}
            height={390}
          />
        </div>
        <div className={styles.right}>
          <h2>考生身份核验</h2>
          <div className={styles.input_box}>
            <Form
              form={form}
              requiredMark={false}
              labelCol={{ span: 4 }}
              labelAlign="left"
              colon={false}
              style={{ position: 'relative' }}
              initialValues={{
                ...userStore.anidInfo,
                phone_areaCode: {
                  phone: userStore.anidInfo?.phone || '',
                  area_code: Number(userStore.anidInfo?.area_code) || 1000,
                },
              }}
            >
              {userStore.anidInfo?.name && (
                <Form.Item
                  name="name"
                  // label="姓名"
                  rules={[{ required: true, message: '请填写姓名' }]}
                >
                  <Input
                    disabled={isHaveAnidInfo}
                    style={{ border: 'none', borderBottom: '1px solid #E2E4EA', paddingBottom: 8 }}
                  />
                </Form.Item>
              )}
              <div style={{ position: 'relative', height: userStore.anidInfo?.name ? 61 : 70 }}>
                <Form.Item
                  name="phone_areaCode"
                  // label="手机号"
                  rules={[
                    {
                      validator(_, value) {
                        if (value.phone.length <= 5) return Promise.reject('请输入正确的手机号码');
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <PhoneInput
                    area_data={area_data}
                    disabled={isHaveAnidInfo && !!userStore.anidInfo?.phone && disabled}
                    ple={userStore.anidInfo?.phone}
                  />
                </Form.Item>
                {isHaveAnidInfo && (
                  <a
                    style={{
                      fontSize: 14,
                      color: '#4e6ef3',
                      cursor: 'pointer',
                      position: 'absolute',
                      right: 3,
                      top: 5,
                    }}
                    onClick={() =>
                      Modal.confirm({
                        className: 'modal',
                        title: '确认要更换吗？',
                        centered: true,
                        content: (
                          <>
                            <p>本场考试核验手机号为：{userStore.anidInfo?.phone}。</p>
                            <p style={{ textAlign: 'justify' }}>
                              更换手机号将被系统记录，并于交卷后告知阅卷人。如因特殊情况必须更换，考生可事后向HR解释情况。
                            </p>
                          </>
                        ),
                        okText: '确定',
                        cancelText: '取消',
                        okButtonProps: {
                          type: 'primary',
                          style: { padding: '4px 15px', fontSize: 14, height: 32 },
                        },
                        async onOk() {
                          sessionStorage.setItem('changePhone', '1');
                          form.setFieldsValue({
                            phone_areaCode: {
                              phone: '',
                              area_code: 1000,
                            },
                          });
                          setDisabled(false);
                        },
                      })
                    }
                  >
                    更换手机号
                  </a>
                )}
              </div>

              <Form.Item dependencies={['phone_areaCode']}>
                {({ getFieldValue }) => {
                  const phone_areaCode = getFieldValue(['phone_areaCode']);
                  return (
                    <Form.Item
                      name="code"
                      rules={[{ required: true, message: '请输入正确的手机验证码' }]}
                    >
                      <CodeInput onGetCode={onGetCode} canGet={phone_areaCode?.phone.length >= 5} />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Form>
          </div>
          <Button className={styles.btn} onClick={onSubmit} loading={loading}>
            登录
          </Button>
        </div>
      </div>
    </Modal>
  );
});
ConfirmPhoneModal.displayName = 'ConfirmPhoneModal';
export default observer(ConfirmPhoneModal);
